<script>
    /**
     * Controls the font size of the number.
     * @type { "x-small" | "small" | "medium" | "large" | "x-large" }
     */
    export let size = "x-small";

    /**
     * Required. The number to be featured, as a string; that is, include commas
     * or any other number styling you want, like `$` to prefix dollar amounts.
     * @type {string}
     */
    export let number;

    /**
     * Required. Label text to render under the number.
     *
     * CSS note: `FeltronNumber` has no width limit built in, so a long label
     * won't wrap unless you constrain the width of the `FeltronNumber`'s
     * container (or make it a flex item or something similar).
     *
     * @type {string}
     */
    export let label;

    /**
     * Header text to render above the number. If omitted, no header text is
     * rendered.
     * @type {string | null}
     */
    export let headerText = null;

    /**
     * URL and text for a link that to render in a `<figcaption>` under the
     * number and label. This can be used to link to context about the number.
     * If `href` is omitted, then the text is rendered without a link. If the
     * whole prop is omitted, then no `<figcaption>` is rendered.
     *
     * Note: The object control in Storybook docs makes you quote object
     * attributes (like JSON), but this is optional for real-world usage.
     *
     * @type { { href: string?, text: string } | null}
     */
    export let caption = null;

    /** @typedef {object} Slider
     * @property {number} rank
     * @property {number} total
     * @property {string} [leftLabel]
     * @property {string} [rightLabel]
     * @property {string} [maxWidth]
     * @see slider for details (docs need to go there for Storybook).
     */
    /**
     * Config to render a slider underneath the number, label and (optional)
     * caption. For example, if you want to show that this number represents
     * rank third out of 20 things, pass in `{ rank: 3, total: 20 }`.
     *
     * If omitted, then no slider is rendered.
     *
     * For this prop, use an object with the following fields:
     * - `rank` (number): the rank of the Feltron number (smaller numbers point
     *    to the left side of the slider)
     * - `total` (number): the total number of things we are ranking this
     *    Feltron number with
     * - `leftLabel` (string, optional): label for the left side of the slider
     *    (if omitted, `High` is used)
     * - `rightLabel` (string, optional): label for the right side of the slider
     *    (if omitted, `Low` is used)
     * - `maxWidth` (string, optional): a `max-width` CSS property for the
     *   slider (if omitted, `140px` is used`)
     *
     * Note: The object control in Storybook docs makes you quote object
     * attributes (like JSON), but this is optional for real-world usage.
     *
     * @type {Slider | null}
     */
    export let slider = null;

    $: sliderPercent = slider ? (slider.rank / slider.total) * 100 : null;
</script>

<figure class="number--{size}">
    {#if headerText}
        <h3>{headerText}</h3>
    {/if}
    <p><strong>{number}</strong> {label}</p>
    {#if slider}
        <div
            class="number__rank-scale"
            role="img"
            aria-label="Chart: {slider.rank} out of {slider.total}"
            style:max-width={slider.maxWidth ?? "140px"}
        >
            <span class="number__rank-marker" style="left: {sliderPercent}%" />
            <ul class="number__rank-labels">
                <li>{slider.leftLabel ?? "High"}</li>
                <li>{slider.rightLabel ?? "Low"}</li>
            </ul>
        </div>
    {/if}
    {#if caption}
        <figcaption>
            {#if caption.href}
                <a href={caption.href}>{caption.text}</a>
            {:else}
                {caption.text}
            {/if}
        </figcaption>
    {/if}
</figure>

<style>figure {
  font-family: var(--fonts-sans);
  font-size: var(--scale-1);
  line-height: var(--line-height-1);
}

h3 {
  font-size: var(--scale-1);
  font-weight: 700;
  text-align: left;
  margin-bottom: var(--spacing-2);
}

p {
  margin-bottom: var(--spacing-1);
}

strong {
  color: var(--color-accent-50);
  display: block;
  font-weight: 700;
  margin-bottom: 0;
}
.number--x-small strong {
  font-size: 1.25em;
}
.number--small strong {
  font-size: 1.75em;
}
.number--medium strong {
  font-size: 2.25em;
}
.number--large strong {
  font-size: 2.75em;
}
.number--x-large strong {
  font-size: 3.25em;
}

figcaption {
  font-size: var(--scale-2);
}

a {
  color: var(--color-accent-70);
  text-decoration-color: var(--color-accent-30);
  display: block;
}
a:hover, a:focus {
  text-decoration-color: var(--color-accent-70);
}

.number__rank-scale {
  position: relative;
  padding-top: 9px;
  margin-top: var(--scale0);
}

.number__rank-marker {
  border-left: 8px solid transparent;
  border-right: 8px solid transparent;
  border-top: 8px solid var(--color-text-body);
  font-size: 0;
  height: 0;
  line-height: 0;
  position: absolute;
  top: 0;
  transform-origin: 50% 100%;
  width: 0;
}

.number__rank-labels {
  border-top: 1px solid var(--color-text-supp);
  font-size: 0.75rem;
  display: flex;
  text-transform: uppercase;
  color: var(--color-text-supp);
  justify-content: space-between;
  margin: 0 0 0.5rem;
  padding: 0.25rem 0 0;
}
.number__rank-labels li {
  list-style: none;
}</style>
